import React, { Fragment, useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'
import withEventDetail from '../../containers/with-event-detail'
import withNewsDetail from '../../containers/with-news-detail'
import withOfferDetail from '../../containers/with-offer-detail'
import withStoryDetail from '../../containers/with-story-detail'
import withModules from '../../containers/with-modules'
import Feature from '../../components/feature'
import Modules from '../../components/modules'
import ComponentMetaData from '../../components/component-meta-data'
import { entryType } from '../../types'
import { parseHostname } from '../../helpers/url'
import pagesHoc from '../pages-hoc'
import getEntryStructuredData from '../../helpers/get-entry-structured-data'
import getEntryOpenGraphData from '../../helpers/get-entry-open-graph-data'
import pageLoadDataLayer from '../../helpers/analytics-utils/page-load-data-layer'
import getAccordionEventDetail, {
  getPastOccurrences,
} from '../../helpers/get-accordion-event-detail'
import { ShareButtonType } from '../../components/page-sharer'
import canonicalLink from '../../helpers/canonical-link'
import { HIDE_UTILITY_BOX } from '../../constants'
import {
  GetHrefContext,
  PromotionPositionContext,
  RavenContext,
} from '../../context'
import useCentreTime from '../../helpers/use-centre-time'
import styles from './index.scss'

function getRetailerUtility(entry, centre) {
  const hideUtility = HIDE_UTILITY_BOX.includes(entry.category)
  if (hideUtility || !entry.store) {
    return null
  }
  const sameLocation = entry.physicalAddress === entry.store.physicalAddress
  return {
    storeLink: `/${centre.slug}/store/${entry.store.sys.id}/${entry.store.slug}`,
    heading: entry.store.storeTitle || 'Visit us',
    phoneNumber: entry.store.phoneNumber,
    emailAddress: entry.store.emailAddress,
    link: entry.store.websiteAddress,
    linkText: parseHostname(entry.store.websiteAddress),
    ...(sameLocation
      ? {
          nearestLocation: entry.store.nearestLocation,
          location: entry.store.physicalAddress,
        }
      : {
          nearestLocation: '',
          location: '',
        }),
    openHours: {
      storeTradingSchedule: entry.store.storeTradingSchedule,
      centreTradingSchedule: centre.centreTradingSchedule,
      timeZone: centre.timeZone,
    },
    status: entry.store.status,
  }
}

function getComponentMetaData(getHref, raven, entry, centre) {
  const url = canonicalLink.canonical(getHref())
  let structuredData = null
  let openGraphData = null
  try {
    openGraphData = getEntryOpenGraphData({ entry, centre, url })
    structuredData = getEntryStructuredData({ entry, centre, url })
  } catch (err) {
    if (raven) {
      raven.captureException(err, { level: 'warning' })
    }
  }
  return { structuredData, openGraphData }
}

const EntryDetail = ({ entry, modules, template, centre }) => {
  const retailerUtility = getRetailerUtility(entry, centre)
  const getHref = useContext(GetHrefContext)
  const raven = useContext(RavenContext)
  const centreTime = useCentreTime()
  const componentMetaData = getComponentMetaData(getHref, raven, entry, centre)
  const validEvent = entry.category === 'Event' && !entry.isExpired
  const emptyOcc = { summary: false, timeZone: '' }
  const shareButtonType = validEvent ? ShareButtonType.EVENT : undefined
  useEffect(() => {
    pageLoadDataLayer(entry, centre)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // This will prevent propType warnings. As the "occurrences" prop may be removed in future, this work around should be ok....
  return (
    <Fragment>
      <Helmet>
        <title>{`${entry.category} - ${entry.title}`}</title>
        <meta name="description" content={entry.shortDescription} />
      </Helmet>
      <ComponentMetaData data={componentMetaData} />
      <PromotionPositionContext.Provider
        value={template?.analyticsMetadata?.displayId}
      >
        <Feature
          centred
          shareButtonType={shareButtonType}
          lazyLoad={false}
          angleOrigin="top-right"
          position="left"
          topic={entry.topic}
          desktopImageUrl={entry.desktopImage && entry.desktopImage.src}
          mobileImageUrl={entry.mobileImage && entry.mobileImage.src}
          title={entry.title}
          body={entry.body}
          priceInfo={entry.priceInfo}
          terms={entry.termsAndConditions}
          retailerUtility={retailerUtility}
          location={validEvent ? '' : entry.location}
          occurrences={validEvent ? emptyOcc : entry.occurrences}
          callsToAction={entry.callsToAction}
          infoAccordion={
            validEvent
              ? getAccordionEventDetail(entry, centre.slug, centreTime)
              : null
          }
          pastEventsAccordion={
            validEvent &&
            entry.occurrences &&
            entry.occurrences.pastTimes.length
              ? getPastOccurrences(entry.occurrences.pastTimes, centreTime)
              : null
          }
          analyticsMetadata={entry.analyticsMetadata}
        />
      </PromotionPositionContext.Provider>
      <Modules className={styles.initialContentGroup} modules={modules} />
    </Fragment>
  )
}

EntryDetail.propTypes = {
  entry: entryType,
  modules: PropTypes.array,
  centre: PropTypes.object,
  template: PropTypes.shape({
    analyticsMetadata: PropTypes.shape({
      displayId: PropTypes.string,
    }),
  }),
}

EntryDetail.defaultProps = {
  modules: [],
}

// TODO: Get this to work if withModules returns error get overall error
const withEntry = containers => withModules(containers(pagesHoc(EntryDetail)))

export const EventDetail = withEntry(withEventDetail)
export const NewsDetail = withEntry(withNewsDetail)
export const OfferDetail = withEntry(withOfferDetail)
export const StoryDetail = withEntry(withStoryDetail)
